import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

export type SeriesInterestRow = {
  id: string;
  section_id: string;
  created_at: string;
  last_digest_at: string | null;
};

/**
 * Bulk-fetches the current user's series interests, plus mutations to
 * register / unregister interest in a section. Mirrors useEventInterest.
 */
export const useSeriesInterest = () => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const { data: interests, isLoading } = useQuery({
    queryKey: ["series-interest", "mine"],
    queryFn: async (): Promise<SeriesInterestRow[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("series_interest")
        .select("id, section_id, created_at, last_digest_at")
        .eq("user_id", user.id);

      if (error) throw error;
      return data ?? [];
    },
    enabled: isAuthenticated,
  });

  const interestedSectionIds = new Set((interests ?? []).map((i) => i.section_id));

  const register = useMutation({
    mutationFn: async (sectionId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("series_interest")
        .insert({ section_id: sectionId, user_id: user.id });

      // Ignore duplicate-key — user is already registered.
      if (error && !String(error.message).toLowerCase().includes("duplicate")) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["series-interest", "mine"] });
    },
  });

  const unregister = useMutation({
    mutationFn: async (sectionId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("series_interest")
        .delete()
        .eq("section_id", sectionId)
        .eq("user_id", user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["series-interest", "mine"] });
    },
  });

  return {
    interests: interests ?? [],
    interestedSectionIds,
    isInterested: (sectionId: string) => interestedSectionIds.has(sectionId),
    register,
    unregister,
    isLoading,
  };
};
